import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useIntl, defineMessages } from 'react-intl';

import { ButtonIcon } from '@companycam/slab-web';

const messages = defineMessages({
  closeButtonAltText: {
    id: 'FloatingActionBar.closeButtonAltText',
    defaultMessage: 'close button',
    description: 'Alt text for close button',
  },
});

const fadeindown = keyframes`
  from {
    translate: 0px -10px;
    opacity: 0;
  }
  to {
    translate: 0px 0px;
    opacity: 1;
  }
`;

const AnimatedFadeContainer = styled.div`
  position: fixed;
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  left: 0;
  right: 0;
  font-variant-numeric: tabular-nums;
  top: 65px; // header height
  justify-content: space-between;
  align-items: center;
  z-index: 99; // Needs a high z-index to ensure that it sits on top of the content on the project screen sidebar
  border-radius: var(--cc_size_border_radius_l);
  padding: var(--cc_size_spacing_s);
  background-color: var(--cc_color_background_2);
  animation-duration: 0.15s;
  animation-name: ${fadeindown};
  margin: var(--cc_size_spacing_m);
  box-shadow: 0 var(--cc_size_depth_3_y) var(--cc_size_depth_3_blur)
    rgba(0, 0, 0, 0.25);

  @media (min-width: 1024px) {
    left: 230px;
    top: 0;
  }
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cc_size_spacing_s);
  flex-wrap: wrap;
`;

type FloatingActionBarProps = {
  isVisible: boolean;
  contentLeft: React.ReactNode;
  contentRight?: React.ReactNode;
  onCloseClick?: () => void;
};

const FloatingActionBar = ({
  isVisible,
  contentLeft,
  contentRight,
  onCloseClick,
}: FloatingActionBarProps) => {
  const { formatMessage } = useIntl();

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatedFadeContainer className="dark-environment">
      <LeftContainer>
        {onCloseClick ? (
          <ButtonIcon
            design="minimal"
            color="secondary"
            accessibilityLabel={formatMessage(messages.closeButtonAltText)}
            iconName="close"
            size="large"
            onClick={onCloseClick}
            data-testid="floating-action-bar__close-button"
          />
        ) : null}

        {contentLeft}
      </LeftContainer>
      {contentRight}
    </AnimatedFadeContainer>
  );
};

export default FloatingActionBar;
